'use client';

import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { lobbyHrefForBoard } from '@/lib/lobby-routes';
import type { MockBoard } from '../data/mock-boards';
import { formatBoardFloor, isBoardNew, isFloorPending } from '../data/mock-boards';
import { BoardTooltip } from './BoardTooltip';
import { BoardCover, boardInitials } from '../ui/BoardCover';
import { PLINTH_PRESETS } from '@/features/lobby/launch/data/presets';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useNowSec } from '@/hooks/use-now-sec';

interface BoardCardProps {
  board: MockBoard;
  active?: boolean;
  onSelect?: () => void;
  /**
   * Show a red unread-chat pip in the top-left corner of the cover.
   * Driven by `useBoardsChatUnread` from `BoardDirectory` and only
   * set on rows in the My Boards list — rendering it elsewhere would
   * dilute the signal into "anyone said anything anywhere", which is
   * already covered by the global chat-sheet bell.
   */
  showUnread?: boolean;
  /**
   * True when the user has just tapped this card and the route swap
   * hasn't completed yet. Renders a darkened spinner overlay so a
   * slow nav doesn't read as an unresponsive tap. Cleared by
   * `LobbyClient.setActiveBoardId`'s effect once `activeBoardId`
   * resolves to this card's id.
   */
  pending?: boolean;
}

export function BoardCard({
  board,
  active,
  onSelect,
  showUnread,
  pending,
}: BoardCardProps) {
  const router = useRouter();
  const plinth = useMemo(() => {
    if (!board.boardPlinth) return null;
    return PLINTH_PRESETS.find((p) => p.id === board.boardPlinth) ?? null;
  }, [board.boardPlinth]);

  // Re-evaluate freshness once a minute so a board on the 4h boundary
  // loses its NEW pill at the right time even if React Query never
  // refetches the lobby (a real concern for boards with no new
  // activity — exactly the ones for which "freshness" matters most).
  // Aligned with the same `useNowSec` tick `useLobbyBoards` uses for
  // its curated/community split, so the bucketing decision and the
  // pill render can never disagree by more than ±1 minute.
  const nowSec = useNowSec(60_000);
  const isNew = isBoardNew(board, nowSec);

  return (
    <Tooltip delayDuration={200} disableTouch>
      <TooltipTrigger asChild>
        <div className="relative group w-full">
          <button
            type="button"
            onClick={() => onSelect?.()}
            aria-current={active ? 'true' : undefined}
            onPointerEnter={() => router.prefetch(lobbyHrefForBoard(board.id))}
            className={cn(
              // Previously `disabled={active}` made the active card
              // unclickable. That broke the lobby-mode flow on mobile:
              // tapping the already-active board inside the full-bleed
              // `BoardDirectory` was a silent no-op instead of closing
              // the lobby overlay and returning to the board. Keeping
              // the click live lets `setActiveBoardId` drop the
              // `?mode=lobby` marker and re-seat the viewer on the same
              // board — its URL-short-circuit already keeps this cheap
              // for no-op cases.
              'relative w-full flex items-stretch gap-2 p-1.5 rounded-[5px] text-left',
              'bg-gradient-to-b from-zinc-900/70 to-black/70 border backdrop-blur-sm',
              'transition-all duration-200',
              active
                ? 'border-amber-400/70 shadow-[0_0_0_1px_rgba(255,215,120,0.2),0_8px_24px_-10px_rgba(255,215,120,0.5)]'
                : 'border-white/[0.06] hover:border-amber-400/40',
            )}
          >
            {/* Plinth slab + centered metadata / generated cover */}
            <div className="relative shrink-0 w-14 h-14 rounded-[5px] overflow-hidden">
              {plinth ? (
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background: plinth.background,
                    boxShadow: `inset 0 0 0 1px ${plinth.edge}55`,
                  }}
                />
              ) : (
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-90"
                  style={{
                    background: `radial-gradient(circle at 50% 120%, ${board.hue}66, #0a0a0a)`,
                  }}
                />
              )}
              <div className="relative h-full w-full flex items-center justify-center p-2">
                <BoardCover
                  seed={board.id}
                  hue={board.hue}
                  initials={boardInitials(board.name)}
                  imageUrl={board.imageUrl ?? null}
                  size={40}
                  className="shadow-[0_4px_12px_-4px_rgba(0,0,0,0.8)]"
                />
              </div>
              {/* Unread-chat pip. Top-left of the cover slab — the
                  shadow ring + white highlight match the chip-rail
                  dot in `MobilePanelChipRail` so the language stays
                  consistent with the existing chat unread cue
                  elsewhere in the app.
                  `z-20` and `-top-0.5 -left-0.5` anchor the dot just
                  outside the 40×40 `BoardCover` paint surface (which
                  can render an `<img>` that creates its own stacking
                  context and otherwise hid the sibling pip on
                  desktop). The `overflow-hidden` on the wrapper is
                  still fine because the dot sits within the 56×56
                  slab's bounds.
                  (Top-right is intentionally empty: the favorite-
                  star affordance was removed pending a future re-
                  wire of the favorites feature.) */}
              {showUnread && (
                <span
                  aria-label="Unread chat messages"
                  className="pointer-events-none absolute top-0 left-0 z-20 h-2.5 w-2.5 rounded-full bg-brand-red shadow-[0_0_0_1.5px_rgba(0,0,0,0.85),0_0_8px_rgba(239,68,68,0.7)]"
                />
              )}
            </div>

            {/* Body */}
            <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-[12px] font-semibold text-zinc-100 truncate group-hover:text-amber-100 transition-colors">
                  {board.name}
                </span>
              </div>
              <div className="text-[10px] text-zinc-500 truncate">{board.tagline}</div>
              <div className="flex items-center gap-2 text-[9px] font-mono tracking-[0.1em] uppercase min-w-0">
                <span className="text-zinc-400 shrink-0 inline-flex items-center gap-1">
                  Floor{' '}
                  {/* Skeleton stripe while the USD oracle is in flight —
                      mirrors the pinned BoardroomCard treatment so a row
                      of community cards reads as "values pending" rather
                      than a flash of raw token amounts. */}
                  {isFloorPending(board.floor) ? (
                    <span
                      aria-busy="true"
                      aria-label="Floor price loading"
                      className="inline-block h-2.5 w-8 rounded-[2px] bg-amber-500/15 animate-pulse"
                    />
                  ) : (
                    <span className="text-zinc-100">{formatBoardFloor(board.floor)}</span>
                  )}
                </span>
                {/* The freshly-launched window still surfaces a NEW
                    pill so a brand-new board reads as "just launched,
                    go look" rather than blending into the established
                    rows. The previous 7D-rewards chip alongside it
                    was removed — the row's economic signal is now
                    surfaced via the directory's sort + tooltip
                    instead, so the card stays readable at a glance
                    with just floor + (optional) NEW. */}
                {isNew && (
                  <span
                    className="min-w-0 truncate rounded-[2px] border border-cyan-400/60 bg-cyan-950/40 px-1.5 py-0.5 text-cyan-100 font-semibold tracking-[0.18em] shadow-[0_0_10px_-2px_rgba(34,211,238,0.55)]"
                    title="Created less than 4 hours ago"
                  >
                    NEW
                  </span>
                )}
              </div>
            </div>

            {/* Active indicator bar */}
            {active && (
              <span
                aria-hidden
                className="absolute left-0 top-1 bottom-1 w-[2px] rounded-full bg-gradient-to-b from-amber-300 via-amber-400 to-amber-500"
              />
            )}
            {/* Tap-to-navigate loading overlay. Pointer-events stay
                disabled so a stuck overlay never traps further input;
                the parent's 5s safety timer is the upper bound. */}
            {pending && (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-[5px] bg-black/55 backdrop-blur-[1px]"
              >
                <Loader2 className="h-4 w-4 animate-spin text-amber-200" />
              </span>
            )}
          </button>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="start"
        sideOffset={10}
        collisionPadding={12}
        className="z-[200] max-w-none border-0 bg-transparent p-0 text-left shadow-none"
      >
        <BoardTooltip board={board} />
      </TooltipContent>
    </Tooltip>
  );
}
